import GoogleAnalyticsEvent from 'dataTransferObjects/googleAnalyticsEvent';
import GoogleAnalyticsCustomDimensions from 'constants/googleAnalyticsCustomDimension';
import { createMarkerHashedKey } from 'libraries/mapLibrary';
import { interactiveSitePlanSvg } from 'constants/svg-assets';
import {
  callForPriceHomeSvgHtml,
  priceReducedSvgHtml,
  callForPriceHomeActiveSvgHtml,
  markerTipSvgHtml
} from '../constants/svgIcons';
import MarkerType from '../enums/markerType';
import { isPlanViewed } from '../services/markerSessionService';
import getClassesToApply from '../services/unitMarkerService';

/* Creates a new instance of a housing unit that will be plotted in the map as a marker. */
export default class PlanUnitMarker {
  constructor(housing) {
    const totalPlans = housing.TotalPlans ?? 1;
    this.geoId = housing.GeoId;
    this.detailText = housing.DetailText;
    this.type = MarkerType.PlanUnit.toString();
    this.id = housing.Id;
    this.communityId = housing.CommunityId;
    this.latitude = housing.Latitude;
    this.longitude = housing.Longitude;
    // TODO: NewHomeSource Rebrand
    // Change the class on the backend.
    this.priceText = housing.PriceText.replace(
      'small--text',
      'nhs-c-tooltip__price-label--small'
    );
    this.shortPrice = housing.ShortPrice;
    this.title = housing.Title;
    this.totalPlans = +totalPlans;
    this.priceReducedText = housing.PriceReducedText;
    this.isPriceReduction = !!housing.PriceReducedText;
    this.previousPriceText = housing.PreviousPriceText;
    this.priceReducedTrackingLabel = housing.PriceReducedTrackingLabel;
    this.price = housing.Price;
    this.hasInteractiveSitePlan = housing.HasInteractiveSitePlan;
    this.sitePlanCenterlatitude = housing.SitePlanCenterLatitude;
    this.sitePlanCenterlongitude = housing.SitePlanCenterLongitude;
    this.isBasic = housing.IsBasic || housing.IsBl;
    this.isFeaturedListing = housing.IsFeaturedListing;
    this.isCallForPrice = housing.IsCallForPrice;
    this.isMasterPlannedCommunity = housing.IsMasterPlannedCommunity || false;
    this.isAdult = housing.IsAdult || false;
    this.isViewed = isPlanViewed(housing.Id);

    if (this.isBasic) {
      this.zIndex = 1;
    } else if (this.isFeaturedListing) {
      this.zIndex = 5;
    } else {
      this.zIndex = 2;
    }
  }

  /**
   * getMarkerText: gets the text the marker is going to show once rendered.
   * @returns a text to show on the map once rendered.
   */
  getMarkerText() {
    return this.shortPrice;
  }

  /**
   * getMarkerTextNode: gets the html node to represent the marker text
   * @returns a string representing the html node to render a marker text
   */
  getMarkerTextNode() {
    if (this.shortPrice && !this.isCallForPrice) {
      return `<div data-marker-text class="nhs-c-marker__text" data-qa="marker_price">
                ${this.shortPrice}
              </div>`;
    }

    return '';
  }

  /**
   * @returns The name of the extend marker type.
   */
  getType() {
    return this.type;
  }

  /**
   * getInteractiveSitePlanSection: adds a <p> with the information needed to reach
   * the interactive site plan of the marker, if any.
   */
  getInteractiveSitePlanSection() {
    if (!this.hasInteractiveSitePlan) {
      return '';
    }

    return `<button
              class="nhs-c-button--text-only nhs-c-button--primary-ui nhs-c-button--small-32 nhs-u-flex-row nhs-u-gap--200"
              data-interactive-plan-button
              data-latitude="${this.sitePlanCenterlatitude}"
              data-longitude="${this.sitePlanCenterlongitude}"
              data-community-id="${this.communityId}">
              <span class='nhs-c-icon--16'>${interactiveSitePlanSvg}</span> Interactive Map
          </button>`;
  }

  /**
   * @returns {string} The HTML string for the card element to be associated to this housing unit marker.
   */
  getCardElementDefault() {
    return `
      <div class="nhs-c-tooltip--map"
          data-id="${this.id}"
          data-tooltip="true"
          data-marker-styles="${this.getMarkerStyles()}">
        <div class="nhs-c-tooltip__title-container">${
          this.shortPrice || this.priceText
        }</div>
        ${this.getFacts()}
        ${this.getInteractiveSitePlanSection()}
      </div>
    `;
  }

  /**
   * @returns {string} The HTML string for the card element to be associated to this housing unit marker,
   * including price reduction information.
   */
  getCardElementWithPriceReduction() {
    return `
    <div class="nhs-c-tooltip--map"
        data-id="${this.id}"
        data-tooltip="true"
        data-marker-styles="${this.getMarkerStyles().join(' ')}">
      <div class="nhs-c-tooltip__title-container">
        ${this.shortPrice || this.priceText}
        <span class="nhs-c-tooltip__price-label--small-strikethrough" data-qa="strike-thru-price">
          ${this.previousPriceText}
        </span>
      </div>
      <p class="nhs-c-tooltip__price-reduction" data-qa="price-reduction">
        <span class='nhs-c-tooltip__price-reduction-icon'>${priceReducedSvgHtml}</span>
        ${this.priceReducedText}
      </p>
      ${this.getFacts()}
      ${this.getInteractiveSitePlanSection()}
    </div>
  `;
  }

  getFacts() {
    if (this.isCallForPrice) {
      return '';
    }

    return `<p class='nhs-c-tooltip__facts'>${this.detailText}</p>`;
  }

  /**
   * It returns a string that is the url to the detail page of the home plan
   * @returns The string `/homedetail/planid-${this.id}`
   */
  getDetailUrl() {
    return `/homedetail/planid-${this.id}`;
  }

  /**
   * This function returns a new GoogleAnalyticsEvent object with the event category, event action, and
   * event label set.
   * @returns A new instance of the GoogleAnalyticsEvent class.
   */
  getGoogleAnalyticsEventForClicks() {
    const customDimensions = {
      [GoogleAnalyticsCustomDimensions.PriceReduction().getName()]:
        this.priceReducedTrackingLabel,
      [GoogleAnalyticsCustomDimensions.PlanId().getName()]: this.id,
      [GoogleAnalyticsCustomDimensions.IsPaid().getName()]: !this.isBasic
    };
    const url = this.getDetailUrl();
    return new GoogleAnalyticsEvent(
      'Map-Pin-Clicks',
      'Homes: SRP-Map-Home-Clicks',
      url,
      customDimensions
    );
  }

  /**
   * This function returns a new meta data object with the required information based on the map pin data.
   * @returns A new instance of the meta data object.
   */
  getHousingLocationMetaData() {
    return {
      priceReducedTrackingLabel: this.priceReducedTrackingLabel,
      planId: this.id,
      isPaid: !this.isBasic,
      priceLow: this.price,
      hasInteractiveSitePlan: this.hasInteractiveSitePlan,
      communityId: this.communityId,
      id: this.id
    };
  }

  /**
   * This function returns the HTML in charge of build the hover card of a given marker
   * @returns hover card HTML
   */
  getCard() {
    if (this.priceReducedText) {
      return this.getCardElementWithPriceReduction();
    }

    return this.getCardElementDefault();
  }

  /**
   * @returns {string} The complete HTML that represents the housing unit marker as an icon and a card with details.
   */
  getIcon() {
    const htmlPinString = this.getHtmlPinElement();
    const cardString = this.getCard();
    return `
      <div class="nhs-c-advance-marker nhs-c-tooltip-hide nhs-mq-tooltip-show--large">
        ${htmlPinString}
        ${cardString}
      </div>
    `;
  }

  /**
   * getMarkerIcon: gets the html in charge of representing the marker icon given some important attribute of it,
   * like being a price reduction marker, call for price marker, among others.
   * @returns a html string representing the marker icon.
   */
  getMarkerIcon() {
    if (this.isCallForPrice) {
      return `
      <div class="nhs-c-marker__icon-no-price">
        <div class="nhs-c-marker__icon--default">${callForPriceHomeSvgHtml}</div>
        <div class="nhs-c-marker__icon--hover">${callForPriceHomeActiveSvgHtml}</div>
      </div>`;
    }

    if (this.priceReducedText) {
      return `<div class="nhs-c-marker__icon">${priceReducedSvgHtml}</div>`;
    }

    return '';
  }

  /**
   * getDescription: returns a description that will help screen readers
   * provide a better explanation about the marker itself.
   * @returns a description for the marker.
   */
  getDescription() {
    if (this.shortPrice) {
      return `Plan from ${this.shortPrice}`;
    }

    return this.title;
  }

  /**
   * getMarkerStyles function is in charge of returning an array with the classes to use for the map marker.
   * @returns an array representing all the classes we should apply to the map marker in terms of its characteristics.
   */
  getMarkerStyles() {
    const { base } = getClassesToApply({
      isMasterPlannedCommunity: this.isMasterPlannedCommunity,
      isSpotlight: this.isFeaturedListing,
      isAdult: this.isAdult,
      isViewed: this.isViewed
    });

    const classesToApply = [...base].filter(Boolean);
    return classesToApply;
  }

  /**
   * Creates an HTML string representing a pin marker of the plan unit.
   * For basic markers, returns a minimal div without arrows or inner content.
   * For all others, includes marker icon, text, and arrow elements.
   * @returns {string} The HTML string for the marker pin.
   */
  getHtmlPinElement() {
    const markerStyles = this.getMarkerStyles().join(' ');
    const dataAttributes = [
      `data-marker-icon="true"`,
      `class="${markerStyles}"`,
      `data-id="${this.id}"`,
      `data-type="${this.getType()}"`,
      `data-geographic-key="${createMarkerHashedKey(
        this.latitude,
        this.longitude
      )}"`,
      `data-price-reduced-tracking-label="${this.priceReducedTrackingLabel}"`,
      `data-is-spotlight="${this.isFeaturedListing}"`,
      `data-is-no-price="${this.isCallForPrice}"`,
      `data-viewed="${this.isViewed}"`,
      `data-is-price-reduction="${this.isPriceReduction}"`,
      `data-qa="marker_container"`,
      this.isBasic ? `data-is-basic="true"` : `data-is-paid="true"`
    ].join(' ');

    if (!this.shortPrice || this.isCallForPrice) {
      return `<div ${dataAttributes}></div>`;
    }

    return `
      <div ${dataAttributes}>        
        <div class="nhs-c-marker">
          <div class="nhs-c-marker__box">
            ${this.getMarkerTextNode()}
            <div class="nhs-c-marker__arrow--tip">${markerTipSvgHtml}</div>
          </div>
        </div>
      </div>
    `;
  }
}
